import React, { useEffect, useState } from "react";
import axios from "axios";
import { useLocation, useNavigate } from "react-router-dom";
import { CheckCircle2, Loader2, ShieldCheck, Send, AlertCircle } from "lucide-react";
import PageHero from "../components/common/PageHero";
import { Reveal } from "../components/common/Section";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Checkbox } from "../components/ui/checkbox";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";
import { toast } from "sonner";
import { track, EVENTS } from "../lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

const CONTROL_AREAS = [
  "Customer-facing AI",
  "Employee AI usage",
  "AI copilots",
  "AI agents",
  "Regulated calls",
  "Sensitive data",
  "Multiple AI systems",
  "In-country / sovereign deployment",
  "Other",
];

const initial = {
  full_name: "",
  email: "",
  company: "",
  role: "",
  enquiry_type: "Book a walkthrough",
  control_area: CONTROL_AREAS[0],
  monthly_volume: "",
  environment: "",
  message: "",
  updates_opt_in: false,
  consent: false,
};

export default function BookDemo() {
  const location = useLocation();
  const [form, setForm] = useState(() => {
    const st = location.state;
    if (!st) return initial;
    return {
      ...initial,
      enquiry_type: st.enquiry || initial.enquiry_type,
      control_area: st.control_area && CONTROL_AREAS.includes(st.control_area) ? st.control_area : initial.control_area,
      message: st.message || initial.message,
      environment: st.message || initial.environment,
    };
  });
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const navigate = useNavigate();
  const [done, setDone] = useState(false);
  const [started, setStarted] = useState(false);

  useEffect(() => { document.title = "Book a walkthrough | norse3"; }, []);

  const sovereign = /sovereign|in-country/i.test(form.enquiry_type);

  const setField = (k, v) => {
    if (!started) { setStarted(true); track(EVENTS.DEMO_FORM_STARTED); }
    setForm((f) => ({ ...f, [k]: v }));
    setErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const e = {};
    if (!form.full_name.trim()) e.full_name = "Please enter your full name.";
    if (!form.email.trim()) e.email = "Please enter your email address.";
    else if (!EMAIL_RE.test(form.email)) e.email = "Please enter a valid email address.";
    if (!form.company.trim()) e.company = "Please enter your company name.";
    if (!form.role.trim()) e.role = "Please enter your job title or role.";
    if (!form.consent) e.consent = "Please confirm you consent to us processing your details.";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const onSubmit = async (ev) => {
    ev.preventDefault();
    if (!validate()) {
      toast.error("Please check the highlighted fields.");
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, message: form.environment || `Walkthrough request — ${form.control_area}` };
      await axios.post(`${API}/demo-requests`, payload);
      track(EVENTS.DEMO_FORM_COMPLETED, { control_area: form.control_area });
      navigate(`/thank-you?type=${sovereign ? "sovereign" : "demo"}`);
    } catch (err) {
      toast.error("Something went wrong. Please try again or email info@norse3.com.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div>
      <PageHero
        eyebrow="Book a walkthrough"
        title="Book a 20-minute norse3 walkthrough."
        subtitle="Tell us what you're looking to control and we'll show norse3 against a realistic scenario — where it sits, how it intervenes and what evidence it creates."
      />

      <section className="py-16 lg:py-20">
        <div className="mx-auto max-w-6xl px-5 lg:px-8 grid lg:grid-cols-5 gap-10">
          <Reveal className="lg:col-span-3">
            {done ? (
              <div className="rounded-lg border border-signal-green/30 bg-signal-green/[0.06] p-10 text-center">
                <CheckCircle2 className="mx-auto h-12 w-12 text-signal-green" />
                <h2 className="mt-5 text-2xl font-bold text-foreground">Request received</h2>
                <p className="mt-3 text-muted-foreground max-w-md mx-auto">
                  Thank you. A member of the norse3 team will review your enquiry and get in
                  touch shortly. If it is urgent, email{" "}
                  <a href="mailto:info@norse3.com" className="text-primary hover:underline">info@norse3.com</a>.
                </p>
                <Button className="mt-7 bg-primary text-primary-foreground hover:bg-primary/90" onClick={() => setDone(false)}>
                  Submit another enquiry
                </Button>
              </div>
            ) : (
              <form onSubmit={onSubmit} noValidate className="rounded-lg border border-white/10 bg-navy-900/60 p-6 sm:p-8">
                {sovereign && (
                  <div className="mb-6 flex items-start gap-3 rounded-xl border border-primary/25 bg-primary/[0.06] p-4" data-testid="demo-sovereign-note">
                    <ShieldCheck className="mt-0.5 h-5 w-5 shrink-0 text-primary" />
                    <p className="text-sm text-foreground">You're enquiring about an <span className="font-semibold">in-country / sovereign deployment</span>. We'll route this to the right specialist so your data-residency requirements are handled from the first conversation.</p>
                  </div>
                )}
                <div className="grid sm:grid-cols-2 gap-5">
                  <Field label="Full name" required error={errors.full_name}>
                    <Input value={form.full_name} onChange={(e) => setField("full_name", e.target.value)} placeholder="Jane Doe" className="bg-navy-800 border-white/12" />
                  </Field>
                  <Field label="Email address" required error={errors.email}>
                    <Input type="email" value={form.email} onChange={(e) => setField("email", e.target.value)} placeholder="jane@firm.co.uk" className="bg-navy-800 border-white/12" />
                  </Field>
                  <Field label="Company name" required error={errors.company}>
                    <Input value={form.company} onChange={(e) => setField("company", e.target.value)} placeholder="Firm Ltd" className="bg-navy-800 border-white/12" />
                  </Field>
                  <Field label="Job title / role" required error={errors.role}>
                    <Input value={form.role} onChange={(e) => setField("role", e.target.value)} placeholder="Head of Compliance" className="bg-navy-800 border-white/12" />
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="What are you looking to control?" required>
                    <Select value={form.control_area} onValueChange={(v) => setField("control_area", v)}>
                      <SelectTrigger className="bg-navy-800 border-white/12" data-testid="demo-control-area">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent className="bg-navy-800 border-white/12">
                        {CONTROL_AREAS.map((t) => (
                          <SelectItem key={t} value={t}>{t}</SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="Approximate monthly AI interactions / calls (optional)">
                    <Input value={form.monthly_volume} onChange={(e) => setField("monthly_volume", e.target.value)} placeholder="e.g. 50,000" className="bg-navy-800 border-white/12" data-testid="demo-volume" />
                  </Field>
                </div>

                <div className="mt-5">
                  <Field label="Tell us about your environment (optional)">
                    <Textarea
                      value={form.environment}
                      onChange={(e) => setField("environment", e.target.value)}
                      placeholder="Which AI systems, channels or workflows you'd like to bring under control."
                      rows={4}
                      className="bg-navy-800 border-white/12 resize-none"
                      data-testid="demo-environment"
                    />
                  </Field>
                </div>

                <div className="mt-6 space-y-4">
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={form.updates_opt_in} onCheckedChange={(v) => setField("updates_opt_in", !!v)} className="mt-0.5 border-white/25" />
                    <span className="text-sm text-muted-foreground">
                      Keep me updated on product features and company news (optional).
                    </span>
                  </label>
                  <label className="flex items-start gap-3 cursor-pointer">
                    <Checkbox checked={form.consent} onCheckedChange={(v) => setField("consent", !!v)} className="mt-0.5 border-white/25" />
                    <span className="text-sm text-muted-foreground">
                      I consent to norse3 processing my details to respond to this enquiry. <span className="text-signal-red">*</span>
                    </span>
                  </label>
                  {errors.consent && (
                    <p className="flex items-center gap-1.5 text-sm text-signal-red">
                      <AlertCircle className="h-4 w-4" /> {errors.consent}
                    </p>
                  )}
                </div>

                <Button type="submit" disabled={submitting} size="lg" data-testid="demo-submit" className="mt-7 w-full sm:w-auto bg-primary text-primary-foreground hover:bg-primary/90">
                  {submitting ? (
                    <><Loader2 className="mr-2 h-4 w-4 animate-spin" /> Sending…</>
                  ) : (
                    <><Send className="mr-2 h-4 w-4" /> Book my walkthrough</>
                  )}
                </Button>
                <p className="mt-4 text-xs text-muted-foreground/70">
                  Investor, press or partner enquiry? <a href="mailto:info@norse3.com" className="text-primary hover:underline">Email us</a> or see the <a href="/company" className="text-primary hover:underline">Company page</a>.
                </p>
              </form>
            )}
          </Reveal>

          <Reveal className="lg:col-span-2" delay={120}>
            <div className="rounded-lg border border-white/10 bg-navy-900/60 p-7 h-full">
              <ShieldCheck className="h-6 w-6 text-primary" />
              <h3 className="mt-4 text-lg font-semibold text-foreground">What to expect</h3>
              <ul className="mt-4 space-y-3 text-sm text-muted-foreground">
                <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> A walkthrough of the control and evidence path across your conversations.</li>
                <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> How norse3 identifies, decides, acts, evidences and supports review.</li>
                <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Where it fits alongside your models, cloud and chatbots.</li>
                <li className="flex items-start gap-2"><span className="text-primary mt-0.5">•</span> Deployment options and how humans stay in control.</li>
              </ul>
              <p className="mt-6 text-xs text-muted-foreground/80">
                norse3 supports alignment and is designed to reduce risk. It is not FCA-approved,
                a guarantee of compliance or a replacement for your compliance function.
              </p>
            </div>
          </Reveal>
        </div>
      </section>
    </div>
  );
}

function Field({ label, required, error, children }) {
  return (
    <div>
      <Label className="text-sm text-foreground">
        {label} {required && <span className="text-signal-red">*</span>}
      </Label>
      <div className="mt-1.5">{children}</div>
      {error && (
        <p className="mt-1.5 flex items-center gap-1.5 text-sm text-signal-red">
          <AlertCircle className="h-4 w-4" /> {error}
        </p>
      )}
    </div>
  );
}
